import React from "react";
import { Box, Text, type Omittable } from "@salestrak/ui";

import type { EntityData } from "#app/lib/entity-visuals";

import { EntityVisual } from "./entity-visual";

/**
 * The identity header of a hover preview: the one part of the card grammar
 * that shows SalesTrak's own kinds of thing. The frame, seam, facts and footer
 * are the library's `PreviewCard*` parts.
 */

/** Identity header shared by every card: name + subline leading (centered
 *  on the visual's vertical axis), the large visual trailing — the reference
 *  member-card layout. A kind with no identity mark of its own (a project)
 *  passes `visual={null}` and the header is the two text lines alone. */
export function IdentityHeader({
  entity,
  title,
  subtitle,
  presence,
  visual,
}: Omittable<{
  entity: EntityData;
  title: string;
  subtitle?: string | null;
  presence?: boolean;
  /** Replaces the entity visual; `null` renders none. */
  visual?: React.ReactNode | null;
}>) {
  return (
    <Box
      direction="row"
      align="center"
      justify="between"
      gap="md"
      minWidth={248}
      paddingY="sm"
    >
      {/* The card is capped, so an identity long enough to fill it truncates
          and offers the rest on hover — an invited member's name falls back to
          their email address, which routinely outruns the card. The column
          holds the visual's 48px whether or not a visual (or a subline that
          may still be loading) is beside it, so the header never grows as the
          card hydrates. */}
      <Box shrink flex={1} gap="2xs" minHeight={48} justify="center">
        <Text overflowTooltip size="base" weight="semibold" truncate={1}>
          {title}
        </Text>
        {subtitle ? (
          <Text overflowTooltip size="xs" variant="muted" truncate={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {visual === null ? null : (
        <Box shrink={false}>
          {visual ?? <EntityVisual entity={entity} size="lg" presence={presence} />}
        </Box>
      )}
    </Box>
  );
}
